import { Alert, Box, Button, Step, StepLabel, Stepper } from '@mui/material';
import React from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { calculateDistributionLines } from '../../domain/distributionCalc';
import { date, DEMO_TODAY } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { KeyValueGrid, PageHeader, SectionCard, StatusChip } from '../../ui/primitives';
import AbaFilePreview from './AbaFilePreview';
import DistributionPaymentsCard from './DistributionPaymentsCard';
import DistributionPreview from './DistributionPreview';

const FLOW = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'PAID'];

export default function DistributionDetailPage() {
  const { distributionId } = useParams();
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const distribution = scope.distributions.find((item) => item.id === distributionId);
  if (!distribution) return <Navigate to="/manager/distributions" replace />;
  const fund = scope.fundById.get(distribution.fundId)!;
  const unitClass = scope.classById.get(distribution.classId)!;
  const components = distribution.components;

  const recalculate = () => {
    const lines = calculateDistributionLines(
      {
        classId: distribution.classId,
        recordDate: distribution.recordDate > DEMO_TODAY ? DEMO_TODAY : distribution.recordDate,
        centsPerUnit: distribution.centsPerUnit,
        components,
        drpEnabled: fund.basis === 'NAV_UNITS',
      },
      state.ledger,
      scope.holders,
    );
    dispatch({ type: 'DISTRIBUTION_SAVE', distribution: { ...distribution, lines } });
    toast('Recalculated from the current register');
  };

  return (
    <>
      <PageHeader
        eyebrow={`${fund.name} · ${unitClass.name}`}
        title={
          <>
            {fund.code} distribution · period ending {date(distribution.periodEnd)}{' '}
            <StatusChip status={distribution.status} />
          </>
        }
        subtitle={`${distribution.centsPerUnit} cents per unit · record date ${date(distribution.recordDate)} · payable ${date(distribution.paymentDate)}`}
        actions={
          <>
            {distribution.status === 'DRAFT' && (
              <>
                <Button variant="outlined" onClick={recalculate}>
                  Recalculate
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    dispatch({ type: 'DISTRIBUTION_SUBMIT', id: distribution.id });
                    toast('Submitted for approval');
                  }}
                >
                  Submit for approval
                </Button>
              </>
            )}
            {distribution.status === 'PENDING_APPROVAL' && (
              <Button component={RouterLink} to="/manager/approvals" variant="contained">
                Open approvals
              </Button>
            )}
            {distribution.status === 'APPROVED' && (
              <Button
                variant="contained"
                onClick={() => {
                  dispatch({ type: 'DISTRIBUTION_PAY', id: distribution.id });
                  toast(
                    'Payment released: ledger posted and advices published to the investor app',
                  );
                }}
              >
                Release payment
              </Button>
            )}
            {distribution.status === 'PAID' && (
              <Button
                variant="outlined"
                onClick={() => toast('Advices (PDF bundle) downloaded', 'info')}
              >
                Download advices
              </Button>
            )}
          </>
        }
      />
      <Stepper activeStep={Math.max(0, FLOW.indexOf(distribution.status)) + 1} sx={{ mb: 3 }}>
        <Step>
          <StepLabel optional={distribution.createdBy}>Calculated</StepLabel>
        </Step>
        <Step>
          <StepLabel>Submitted</StepLabel>
        </Step>
        <Step>
          <StepLabel optional={distribution.approvedBy}>Approved</StepLabel>
        </Step>
        <Step>
          <StepLabel
            optional={distribution.status === 'PAID' ? date(distribution.paymentDate) : undefined}
          >
            Paid
          </StepLabel>
        </Step>
      </Stepper>
      {distribution.status === 'APPROVED' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Approved by {distribution.approvedBy}. Upload {distribution.abaFileName} to the bank, then
          release payment to post the ledger and publish advices. (In phase 2 this goes straight to
          the bank API.)
        </Alert>
      )}
      <DistributionPreview distribution={distribution} />
      <DistributionPaymentsCard distribution={distribution} />
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, mt: 2 }}
      >
        <SectionCard title="Tax components (estimated)">
          <KeyValueGrid
            items={[
              { label: 'Interest', value: `${(components.interest * 100).toFixed(1)}%` },
              {
                label: 'Other Australian income',
                value: `${(components.otherIncome * 100).toFixed(1)}%`,
              },
              {
                label: 'Discounted capital gain',
                value: `${(components.discountedGain * 100).toFixed(1)}%`,
              },
              { label: 'Tax-deferred', value: `${(components.taxDeferred * 100).toFixed(1)}%` },
              { label: 'Foreign income', value: `${(components.foreignIncome * 100).toFixed(1)}%` },
              { label: 'Finalised', value: 'At year end via the AMMA statement' },
            ]}
          />
        </SectionCard>
        <AbaFilePreview distribution={distribution} fund={fund} />
      </Box>
    </>
  );
}
